import { fusoVisualizacao } from '@/utils/fuso';
import { GenericServiceBase } from "../generic-service";

/**
 * Peças comuns das telas de trilha e auditoria do admin (Trilha de Bots,
 * onboarding Meta, webhooks Meta, sincronização de contatos, auditorias).
 * Espelha `src/common/trilha` e `src/modules/trilha-filtros` do servidor.
 */

/** Estado de UMA anomalia numa execução: valor automático + override manual. */
export interface EstadoAnomaliaTrilha {
    auto: boolean;
    manual: boolean | null;
    manualPor?: string | null;
    manualEm?: string | null;
    nota?: string | null;
    detalhe?: string | null;
    ocorrencias?: number;
}

export type MapaAnomaliasTrilha = Record<string, EstadoAnomaliaTrilha | undefined>;

/** Rótulo, ícone e explicação de um tipo de anomalia (ou de qualquer enum mostrado em chip). */
export interface RotuloComAjuda {
    rotulo: string;
    icone: string;
    ajuda: string;
}

/** Telas com filtros salvos — `TELAS_FILTRO_SALVO` em `trilha-filtros.constants.ts` no servidor. */
export type TelaFiltroSalvo =
    | 'bot-trilha' | 'meta-onboarding' | 'meta-webhooks' | 'contatos-trilha' | 'auditoria-admin' | 'auditoria-usuarios';

export interface IFiltroSalvo {
    id: string;
    tela?: TelaFiltroSalvo;
    nome: string;
    query: string;
    descricao: string | null;
    idAdministrador: string;
    nomeAdministrador: string | null;
    criadoEm: string;
    atualizadoEm: string;
}

/** Filtros salvos compartilhados entre os administradores, por tela. */
export class TrilhaFiltrosService extends GenericServiceBase {
    constructor() { super('admin/trilha-filtros') }

    async listar(tela: TelaFiltroSalvo): Promise<IFiltroSalvo[]> {
        return await this.f.get('', { tela }) as IFiltroSalvo[];
    }

    async salvar(tela: TelaFiltroSalvo, dados: { nome: string; query: string; descricao?: string }): Promise<IFiltroSalvo> {
        return await this.f.post('', { tela, ...dados }) as IFiltroSalvo;
    }

    async atualizar(id: string, dados: { nome?: string; query?: string; descricao?: string }): Promise<IFiltroSalvo> {
        return await this.f.patch(`/${id}`, dados) as IFiltroSalvo;
    }

    async remover(id: string): Promise<void> {
        await this.f.delete(`/${id}`);
    }
}

/**
 * Filtro de lista → parâmetros de query da API: tira vazios e junta listas por
 * vírgula. As chaves de data (`desde`/`ate` por padrão) vão como hora de parede
 * (`YYYY-MM-DDTHH:mm` do `datetime-local`), sem conversão, junto com o fuso de
 * visualização em `tz` — o servidor monta o intervalo UTC nesse fuso ("Até"
 * inclusivo até o fim do minuto). `tz` só vai quando há data no filtro.
 */
export function paramsDoFiltro(filtro: object, chavesDeData: readonly string[] = ['desde', 'ate'], tz: string = fusoVisualizacao.value): Record<string, unknown> {
    const saida: Record<string, unknown> = {};
    let temData = false;
    for (const [chave, valor] of Object.entries(filtro)) {
        if (valor === undefined || valor === null || valor === '') continue;
        if (chavesDeData.includes(chave) && typeof valor === 'string') {
            saida[chave] = valor;
            temData = true;
            continue;
        }
        if (Array.isArray(valor)) {
            if (!valor.length) continue;
            saida[chave] = valor.join(',');
            continue;
        }
        saida[chave] = valor;
    }
    if (temData) saida.tz = tz;
    return saida;
}
